import { Link } from "react-router-dom";

function LegalLayout({ title, lastUpdated, children, testId }) {
  return (
    <div className="overflow-x-hidden" data-testid={testId}>
      <section className="pt-32 pb-16" style={{ background: "linear-gradient(135deg, #091F2C 0%, #194449 55%, #1E5C60 100%)" }}>
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="flex items-center gap-2 text-sm mb-5" style={{ color: '#B9DCD2' }}>
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/eventsup-privacy" className="hover:text-white transition-colors">EventsUp Privacy</Link>
          </div>
          <h1 className="text-4xl font-light text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</h1>
          {lastUpdated && <p className="text-sm" style={{ color: '#B9DCD2' }}>Last updated: {lastUpdated}</p>}
        </div>
      </section>
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-16">
        {children}
      </div>
    </div>
  );
}

const Section = ({ num, title, children }) => (
  <div className="mb-10">
    <h2 className="text-xl font-semibold text-gray-900 mb-4 mt-8" style={{ fontFamily: 'Outfit, sans-serif' }}>
      {num && <span className="text-ms-blue mr-2">{num}.</span>}{title}
    </h2>
    {children}
  </div>
);
const P = ({ children }) => <p className="text-gray-600 leading-relaxed mb-4 text-sm">{children}</p>;
const UL = ({ items }) => (
  <ul className="list-disc list-inside space-y-2 text-gray-600 text-sm mb-4 ml-4">
    {items.map((item, i) => <li key={i}>{item}</li>)}
  </ul>
);

export default function EventsUpPrivacy() {
  return (
    <LegalLayout title="Technofocus Privacy Notice for EventsUp Websites" lastUpdated="17 November 2025" testId="eventsup-privacy-page">
      <Section num="1" title="Purpose">
        <P>This Privacy Notice explains how <a href="https://technofocus.ai" className="text-ms-blue hover:underline">Technofocus Pte. Ltd.</a> ("Technofocus," "we," "our," "us") collects, uses, shares, and protects personal data of individuals ("you") who register for or interact with online events hosted by Technofocus through the Cvent event-management platform.</P>
        <P>Technofocus Pte. Ltd. is the <strong>data controller</strong> for this processing. Cvent Inc. acts as our <strong>data processor</strong> and hosts the platform on our behalf.</P>
      </Section>

      <Section num="2" title="What Data We Collect">
        <P>When you register or participate in an event, we may collect:</P>
        <UL items={[
          "Full name, email address, company name, job title, and country/region.",
          "Responses to registration questions or surveys.",
          "Attendance status and session participation metrics.",
          "Technical data (such as browser type, IP address, device identifier, and cookie preferences).",
        ]} />
        <P>We do not intentionally collect sensitive personal data (e.g., health, financial, biometric) on these pages.</P>
      </Section>

      <Section num="3" title="How We Use Your Data">
        <P>We use your data to:</P>
        <UL items={[
          "Register you for the selected event and send transactional emails.",
          "Provide event information, materials, and support.",
          "Generate aggregate attendance or feedback reports for program evaluation.",
          "Improve future events and communications.",
        ]} />
        <P>Any personal attendee information is only shared with Microsoft or other clients (event sponsors) in a password protected manner.</P>
      </Section>

      <Section num="4" title="How We Share Data">
        <P>We share data only with:</P>
        <UL items={[
          "Cvent Inc., our event-management service provider.",
          "Our event partners/sponsors or Microsoft and its partners.",
          "Authorized Technofocus staff involved in event management.",
        ]} />
        <P>We do not sell or disclose your personal data to advertisers or unrelated third parties. Cvent stores data in secure datacentres in accordance with its <a href="https://www.cvent.com/en/cvent-global-privacy-policy" target="_blank" rel="noopener noreferrer" className="text-ms-blue hover:underline">Global Privacy Policy</a>.</P>
      </Section>

      <Section num="5" title="Legal Basis for Processing">
        <P>Processing is based on:</P>
        <UL items={[
          "Contractual necessity — to register and manage your participation.",
          "Consent — for optional communications or non-essential cookies. You may withdraw consent at any time via the opt-out link in our emails or via our cookie banner.",
        ]} />
      </Section>

      <Section num="6" title="Data Retention">
        <P>Personal data is retained only for as long as necessary to deliver the event and related reporting — typically <strong>within 60 days after event closure</strong> — after which records are deleted or anonymized. Consent records and cookie preferences may be retained for audit purposes for up to 13 months.</P>
      </Section>

      <Section num="7" title="Your Rights">
        <P>Depending on applicable law (GDPR or Singapore PDPA), you may have the right to access, correct, delete, restrict, or object to the processing of your personal data and to withdraw consent. To exercise these rights, contact us at <a href="mailto:privacy@technofocus.co" className="text-ms-blue hover:underline">privacy@technofocus.co</a>, specifying the event name and email used for registration.</P>
      </Section>

      <Section num="8" title="Security">
        <P>We apply administrative, technical, and organizational measures to protect your data — including encryption of devices and secure storage within the Cvent platform.</P>
      </Section>

      <Section num="9" title="Cookies">
        <P>Our event websites use cookies to operate and improve performance. You can manage your preferences through the cookie banner or read our full <Link to="/eventsup-cookie-policy" className="font-semibold text-ms-blue hover:text-ms-blue-dark transition-colors">EventsUp Cookie Policy</Link>.</P>
      </Section>

      <Section num="10" title="Contact Us">
        <div className="bg-gray-50 rounded-xl p-5 border border-gray-100">
          <p className="font-semibold text-gray-900 text-sm mb-2">Technofocus Pte. Ltd.</p>
          <p className="text-gray-500 text-sm">138 Robinson Road, #26-01 Oxley Tower, Singapore 068906</p>
          <a href="mailto:privacy@technofocus.co" className="text-ms-blue text-sm hover:underline">privacy@technofocus.co</a>
          <br />
          <a href="https://technofocus.ai" className="text-ms-blue text-sm hover:underline">https://technofocus.ai</a>
        </div>
      </Section>
    </LegalLayout>
  );
}
